import { useCallback, useEffect, useRef } from 'react'
import merge from 'lodash/merge'
import { growthbook } from '@/experimentation/client'
import { usePageVisibility } from '@/hooks/usePageVisibility'
import { useSafeTrack } from '@/utils/analytics'
import { getAnalyticsLocale, getAnalyticsSessionId } from '@/utils/analytics/session-utils'
import { logger } from '@/utils/logging'
import { Maybe } from '@/utils/types'
import {
  ArtworkMediaFormat,
  ContentBlockType,
  ContentInteractionEvent,
  DeepMaybe,
  InteractionAction,
  PlacementContainer,
  PlacementLayout,
  PresentationSurface,
} from './types'
import { validateContentInteractionEvent } from './validation'

// Content interaction tracking constants
export const HOVER_THRESHOLD_MS = 3000 // Track hover after 3 seconds

const interactionsToReportOnlyOnce: InteractionAction[] = ['impression']

interface ShouldTrackInteractionProps {
  event: ContentInteractionEvent
  trackedEventIds: Set<string>
  appState: 'active' | 'background'
}

export const shouldTrackInteraction = ({ event, trackedEventIds, appState }: ShouldTrackInteractionProps) => {
  if (appState !== 'active') {
    return false
  }

  if (interactionsToReportOnlyOnce.includes(event.interaction.action)) {
    // Include session_id in the deduplication key to track once per session
    const sessionId = getAnalyticsSessionId()
    const id = `${event.content.id}:${event.interaction.action}:${event.presentation.surface}:${event.presentation.placement.container}:${sessionId}`
    const alreadyTrackedEvent = trackedEventIds.has(id)
    if (alreadyTrackedEvent) {
      return false
    } else {
      trackedEventIds.add(id)
    }
  }
  return true
}

export abstract class ContentInteraction<TSurface extends PresentationSurface = PresentationSurface> {
  protected event: DeepMaybe<ContentInteractionEvent>

  constructor(item: { id: string; __typename: Maybe<ContentBlockType>; attribution: unknown }) {
    this.event = {
      content: {
        type: item.__typename ?? undefined,
        id: item.id,
      },
      attribution: item.attribution,
    }
  }

  withImpression(): this {
    this.event.interaction = { action: 'impression' }
    return this
  }

  withPress(): this {
    this.event.interaction = { action: 'press' }
    return this
  }

  withHover(dwell_ms: number, hover_video_url?: string): this {
    this.event.interaction = {
      action: 'hover',
      hover: {
        dwell_ms,
        ...(hover_video_url && { hover_video_url }),
      },
    }
    return this
  }

  withSurface(surface: TSurface, freeform_surface?: string): this {
    const surfaceUpdate: DeepMaybe<ContentInteractionEvent> = {
      presentation: {
        surface,
        ...(freeform_surface && { freeform_surface }),
      },
    }

    this.event = merge(this.event, surfaceUpdate)
    return this
  }

  withPlacement(
    container: PlacementContainer,
    containerIndex: number,
    itemIndex: number,
    layout: PlacementLayout,
  ): this {
    const placementUpdate: DeepMaybe<ContentInteractionEvent> = {
      presentation: {
        placement: {
          container,
          container_position: containerIndex + 1,
          item_position: itemIndex + 1,
          layout,
        },
      },
    }

    this.event = merge(this.event, placementUpdate)
    return this
  }

  withImageArtwork(image_url: Maybe<string>, media_format: ArtworkMediaFormat) {
    const artworkUpdate: DeepMaybe<ContentInteractionEvent> = {
      presentation: {
        artwork: {
          image_url: image_url ?? undefined,
          media_format,
        },
      },
    }

    this.event = merge(this.event, artworkUpdate)
    return this
  }

  withVideoArtwork(video_url: Maybe<string>, media_format: ArtworkMediaFormat) {
    const artworkUpdate: DeepMaybe<ContentInteractionEvent> = {
      presentation: {
        artwork: {
          video_url: video_url ?? undefined,
          media_format,
        },
      },
    }

    this.event = merge(this.event, artworkUpdate)
    return this
  }

  get(): DeepMaybe<ContentInteractionEvent> {
    return { ...this.event }
  }
}

export const useContentInteraction = () => {
  const appState = usePageVisibility()
  const track = useSafeTrack()
  const trackedEventIds = useRef(new Set<string>())
  const trackingConfig = useRef<{ enabled: boolean; whitelistSurfaces: string[] }>({
    enabled: false,
    whitelistSurfaces: [],
  })

  useEffect(() => {
    const handleBeforeUnload = () => {
      trackedEventIds.current.clear()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const trackInteraction = useCallback(
    (interaction: ContentInteraction, allowAutoImpression = true) => {
      // Check if feature is enabled using the global growthbook instance
      const isFeatureEnabled = growthbook.getFeatureValue('content_interaction', false)
      if (!isFeatureEnabled) {
        return
      }

      const builtEvent = interaction.get()

      const { isValid, errors, event } = validateContentInteractionEvent(builtEvent)

      const config = trackingConfig.current
      if (config.enabled && !config.whitelistSurfaces.includes(event.presentation.surface)) {
        return
      }

      if (!isValid) {
        logger().error('Invalid Content Interaction Event', { errors, builtEvent })
        return
      }

      if (
        shouldTrackInteraction({
          event,
          trackedEventIds: trackedEventIds.current,
          appState,
        })
      ) {
        const locale = getAnalyticsLocale()
        track('content_interaction', event as unknown as Record<string, unknown>, {
          context: {
            locale,
          },
        })

        if (event.interaction.action === 'press' && allowAutoImpression) {
          trackInteraction(interaction.withImpression(), false)
        }
      }
    },
    [appState, track],
  )

  const resetTracking = useCallback(() => {
    trackedEventIds.current.clear()
  }, [])

  const setTrackingConfig = useCallback((config: unknown) => {
    let enabled = false
    let whitelistSurfaces: string[] = []
    if (typeof config !== 'object' || config === null) {
      return
    }
    if ('enabled' in config && typeof config.enabled === 'boolean') {
      enabled = config.enabled
    }
    if ('whitelistSurfaces' in config && Array.isArray(config.whitelistSurfaces)) {
      whitelistSurfaces = config.whitelistSurfaces
    }
    trackingConfig.current = { enabled, whitelistSurfaces }
  }, [])

  return {
    trackInteraction,
    resetTracking,
    setTrackingConfig,
  }
}
